import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { H3 } from 'UIComponents/elements/headings';
import UIButton from 'UIComponents/button/UIButton';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UILink from 'UIComponents/link/UILink';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIModal from 'UIComponents/dialog/UIModal';
import UICardGrid from 'UIComponents/card/UICardGrid';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIImage from 'UIComponents/image/UIImage';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import { getCredentialDisplayName } from 'marketplace-ui-common/utils/credentials';
import * as FilterKey from '../../constants/search/FilterKey';
// @ts-expect-error module is untyped
import { push } from '../../utils/history';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import { ACCREDITATION_CARD_IDS } from '../../constants/HomepageIconMap';
import { getAccreditations } from '../../selectors/accreditations';
import { fromFilterStateToLocation } from '../../utils/storefrontFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function HomepageAccreditationCardGrid() {
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const [selectedAccreditation, setSelectedAccreditation] = useState(null); // used for definition modal
  const accreditations = useSelector(getAccreditations);
  const lang = getLang();
  const {
    gridColumnCount
  } = useResponsiveProps({
    gridColumnCount: {
      lg: 2,
      sm: 1
    }
  });
  const renderedAccreditationCards = useMemo(() => {
    return ACCREDITATION_CARD_IDS.map(accreditationId => {
      const accreditation = accreditations.filter(acc => acc.id === accreditationId)[0];
      if (!accreditation) {
        return null;
      }
      const accreditationName = getCredentialDisplayName(accreditation, lang);
      const accreditationTrackingName = getCredentialDisplayName(accreditation, 'en');
      return /*#__PURE__*/_jsx(UICardWrapper, {
        children: /*#__PURE__*/_jsx(UICardSection, {
          style: {
            height: '100%'
          },
          children: /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: "md",
            alternate: /*#__PURE__*/_jsxs(UIFlex, {
              style: {
                textAlign: 'center'
              },
              direction: "column",
              justify: "between",
              align: "center",
              children: [/*#__PURE__*/_jsx(UIImage, {
                className: "p-bottom-6",
                src: accreditation.icon,
                width: "125px"
              }), /*#__PURE__*/_jsx(H3, {
                role: "presentation",
                children: accreditationName
              }), /*#__PURE__*/_jsx("p", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `ecosystem-marketplace-solutions-ui.homepage.accreditations.mobileCardSubtitle`,
                  options: {
                    accreditationName
                  }
                })
              }), /*#__PURE__*/_jsx(UIButton, {
                className: "p-left-1",
                onClick: () => setSelectedAccreditation(accreditation),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `ecosystem-marketplace-solutions-ui.homepage.accreditations.whatIs`,
                  options: {
                    accreditationName
                  }
                })
              }), /*#__PURE__*/_jsx(UIButton, {
                onClick: () => {
                  trackInteraction('click homepage accreditation card', {
                    feature: FilterKey.ACCREDITATION,
                    item_type: accreditationTrackingName || accreditation.id
                  });
                  push(fromFilterStateToLocation({
                    accreditation: [accreditation.id.toString(10)]
                  }, []));
                },
                size: "small",
                className: "m-top-4",
                use: "tertiary",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "ecosystem-marketplace-solutions-ui.homepage.accreditations.findButton",
                  options: {
                    accreditationName
                  }
                })
              })]
            }),
            children: /*#__PURE__*/_jsxs(UIFlex, {
              style: {
                height: '100%'
              },
              direction: "column",
              children: [/*#__PURE__*/_jsx(UIMediaObject, {
                align: "stretch",
                itemRight: /*#__PURE__*/_jsx(UIImage, {
                  src: accreditation.icon,
                  width: "125px"
                }),
                children: /*#__PURE__*/_jsxs(UIFlex, {
                  direction: "column",
                  justify: "between",
                  children: [/*#__PURE__*/_jsx(H3, {
                    style: {
                      minHeight: '40px',
                      overflowWrap: 'anywhere'
                    },
                    role: "presentation",
                    children: accreditationName
                  }), /*#__PURE__*/_jsxs("p", {
                    children: [/*#__PURE__*/_jsx(FormattedMessage, {
                      message: `ecosystem-marketplace-solutions-ui.homepage.accreditations.accreditation${accreditation.id}.shortDescription`
                    }), ' ', /*#__PURE__*/_jsx(UIButton, {
                      onClick: () => {
                        trackInteraction('click homepage accreditation card learn more', {
                          feature: FilterKey.ACCREDITATION,
                          item_type: accreditationTrackingName || accreditation.id
                        });
                        setSelectedAccreditation(accreditation);
                      },
                      use: "link",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: `ecosystem-marketplace-solutions-ui.homepage.accreditations.learnMore`
                      })
                    })]
                  })]
                })
              }), /*#__PURE__*/_jsx(UIButton, {
                onClick: () => {
                  trackInteraction('click homepage accreditation card', {
                    feature: FilterKey.ACCREDITATION,
                    item_type: accreditation.id
                  });
                  push(fromFilterStateToLocation({
                    accreditation: [accreditation.id.toString(10)]
                  }, []));
                },
                size: "small",
                className: "m-top-4",
                use: "tertiary",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "ecosystem-marketplace-solutions-ui.homepage.accreditations.findButton",
                  options: {
                    accreditationName
                  }
                })
              })]
            })
          })
        })
      }, accreditation.id);
    });
  }, [accreditations, lang, trackInteraction]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(H3, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.accreditations.title"
      }), /*#__PURE__*/_jsx(UIHelpIcon, {
        className: "p-left-4",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.homepage.accreditations.tooltip"
        }),
        tooltipPlacement: "right"
      })]
    }), /*#__PURE__*/_jsxs("p", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.accreditations.subtitle"
      }), /*#__PURE__*/_jsx(UILink, {
        className: "p-left-1",
        href: "https://academy.hubspot.com/accreditations",
        external: true,
        onClick: () => trackInteraction('click accreditation learn more'),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.homepage.accreditations.learnMore"
        })
      })]
    }), /*#__PURE__*/_jsx(UIExpandableText, {
      fadeOutHeight: 50,
      maxHeight: 425,
      onExpandButtonClick: () => {
        trackInteraction('click to expand accreditation see more');
      },
      children: /*#__PURE__*/_jsx(UICardGrid, {
        className: "m-bottom-6",
        columns: gridColumnCount,
        children: renderedAccreditationCards
      })
    }), selectedAccreditation && /*#__PURE__*/_jsxs(UIModal, {
      use: "conversational",
      width: 600,
      centered: false,
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        style: {
          textAlign: 'left'
        },
        children: [/*#__PURE__*/_jsx(H3, {
          className: "p-bottom-4",
          children: getCredentialDisplayName(selectedAccreditation, lang)
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: () => setSelectedAccreditation(null)
        })]
      }), /*#__PURE__*/_jsx(UIDialogBody, {
        style: {
          textAlign: 'left'
        },
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `ecosystem-marketplace-solutions-ui.homepage.accreditations.accreditation${selectedAccreditation.id}.fullDescription`
        })
      })]
    })]
  });
}